// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import L from 'leaflet';
import type { AppContext, Parliamentarian } from '../types';
import { fmtDollars, fmtMoney, escapeHtml, stateName } from '../format';
import { partyStyle } from '../parties';
import { titleCaseSeat } from '../ui';

// Sequential green ramp (light -> deep) for the choropleth.
const RAMP = ['#e6f0ea', '#bfe0cd', '#8ec7a5', '#57a97b', '#2b8b5b', '#0d5f3d'];

function quantileBreaks(values: number[], n: number): number[] {
  const v = [...values].sort((a, b) => a - b);
  const breaks: number[] = [];
  for (let i = 1; i < n; i++) breaks.push(v[Math.floor((i / n) * v.length)] ?? v[v.length - 1] ?? 0);
  return breaks;
}

export async function renderMap(root: HTMLElement, ctx: AppContext): Promise<void> {
  const { data } = ctx;
  const totals = data.summary.electorateTotals;

  // Map each electorate (uppercase) to its representative — prefer the current
  // House member, else the highest-spending one recorded for that division.
  const memberByDiv = new Map<string, Parliamentarian>();
  for (const p of data.people) {
    if (p.chamber !== 'House' || !p.electorate) continue;
    const key = p.electorate.toUpperCase();
    const cur = memberByDiv.get(key);
    if (!cur) { memberByDiv.set(key, p); continue; }
    const better = (p.isCurrent && !cur.isCurrent) || (p.isCurrent === cur.isCurrent && p.total > cur.total);
    if (better) memberByDiv.set(key, p);
  }

  root.innerHTML = `
    <div class="view-head">
      <h2>The spending map</h2>
      <p>Each of the 150 federal electorates, shaded by the total work expenses of its House member since the 2022 election. Hover for detail, click a division to open its member. Senators represent whole states and are shown in the totals below.</p>
    </div>
    <div class="map-shell">
      <div class="map-canvas" id="map-canvas"></div>
      <div class="map-legend" id="map-legend"></div>
    </div>
    <p class="map-note">Divisions in grey had no House member recorded in the data (abolished at the 2025 redistribution, or Senate-only representation). This map covers the House of Representatives; use the state totals for a whole-of-parliament picture.</p>
    <div class="chart-grid" style="margin-top:var(--space-lg)">
      <div class="chart-card">
        <h3>By state &amp; territory</h3>
        <div class="subtitle">Combined expenses of all parliamentarians based in each state or territory.</div>
        <div class="bar-list" id="state-bars"></div>
      </div>
    </div>`;

  const values = Object.values(totals).filter((v) => v > 0);
  const breaks = quantileBreaks(values, RAMP.length);
  const colourFor = (v: number | undefined): string => {
    if (v === undefined || v <= 0) return '#d9d5ca';
    for (let i = 0; i < breaks.length; i++) if (v <= breaks[i]) return RAMP[i];
    return RAMP[RAMP.length - 1];
  };

  const canvas = document.getElementById('map-canvas') as HTMLElement;
  const map = L.map(canvas, { minZoom: 3, maxZoom: 11, zoomControl: true, scrollWheelZoom: false, attributionControl: true });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', {
    attribution: 'Tiles © CARTO · Boundaries: Digital Atlas of Australia (CC BY 4.0) · Expenses: IPEA',
    subdomains: 'abcd', minZoom: 3, maxZoom: 11,
  }).addTo(map);

  let geo: GeoJSON.FeatureCollection;
  try {
    geo = await fetch('data/electorates.geojson').then((r) => {
      if (!r.ok) throw new Error('Could not load boundaries');
      return r.json();
    });
  } catch (err) {
    root.querySelector('.map-shell')!.innerHTML = `<div class="error-box">Couldn't load the electorate map.<br /><small>${(err as Error).message}</small></div>`;
    return;
  }

  const layer = L.geoJSON(geo, {
    style: (f) => {
      const div = ((f?.properties as { div?: string })?.div || '').toUpperCase();
      return { fillColor: colourFor(totals[div]), fillOpacity: 0.82, color: '#ffffff', weight: 0.6 };
    },
    onEachFeature: (f, lyr) => {
      const div = ((f.properties as { div?: string }).div || '').toUpperCase();
      const nameDisp = titleCaseSeat(div);
      const total = totals[div];
      const member = memberByDiv.get(div);
      const html = member
        ? `<strong>${escapeHtml(nameDisp)}</strong><br>${escapeHtml(member.name)} · ${escapeHtml(partyStyle(member.partyCode).name)}<br>${fmtDollars(total ?? 0)}`
        : `<strong>${escapeHtml(nameDisp)}</strong><br><em>No House member in data</em>`;
      lyr.bindTooltip(html, { sticky: true, className: 'map-tip' });
      lyr.on({
        mouseover: () => (lyr as L.Path).setStyle({ weight: 2, color: '#1c3a5e' }),
        mouseout: () => layer.resetStyle(lyr),
        click: () => { if (member) ctx.openMp(member.slug); },
      });
    },
  }).addTo(map);

  // Legend
  const legend = document.getElementById('map-legend')!;
  const fmtBreak = (i: number) => {
    const lo = i === 0 ? 0 : breaks[i - 1];
    const hi = i < breaks.length ? breaks[i] : Infinity;
    return hi === Infinity ? `${fmtMoney(lo)}+` : `${fmtMoney(lo)}–${fmtMoney(hi)}`;
  };
  legend.innerHTML =
    `<div class="title">House member total</div>` +
    RAMP.map((c, i) => `<div class="row"><span class="sw" style="background:${c}"></span>${fmtBreak(i)}</div>`).join('') +
    `<div class="row"><span class="sw" style="background:#d9d5ca"></span>No data</div>`;

  // State bars
  const states = data.summary.states;
  const smax = Math.max(...states.map((x) => x.total), 1);
  document.getElementById('state-bars')!.innerHTML = states
    .map((st) => {
      const w = (st.total / smax) * 100;
      return `<div class="bar-row" style="cursor:default" data-tip="${escapeHtml(stateName(st.code))}: ${fmtDollars(st.total)}">
        <div class="bar-rank">${st.code}</div>
        <div class="bar-main">
          <div class="bar-label"><span class="bar-name">${escapeHtml(stateName(st.code))}</span><span class="bar-value">${fmtDollars(st.total)}</span></div>
          <div class="bar-track"><div class="bar-fill" style="width:${w.toFixed(1)}%;background:var(--ink)"></div></div>
        </div>
      </div>`;
    })
    .join('');

  // Zero-size defence.
  const bounds = layer.getBounds();
  const fit = () => {
    map.invalidateSize();
    if (bounds.isValid() && canvas.clientHeight > 50) map.fitBounds(bounds, { padding: [10, 10] });
  };
  const ro = new ResizeObserver(() => { if (canvas.clientHeight > 50) { fit(); ro.disconnect(); } });
  ro.observe(canvas);
  setTimeout(fit, 400);
}
